'use client';

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Toaster, toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import Modal from '@/components/ui/Modal';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import Breadcrumb from '@/components/Breadcrumb';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

export interface IPRecord {
  id: string;
  ip: string;
  ptr: string | null;
  subnet_id: string | null;
  vlan_id: string | null;
  hostname: string | null;
  mac_address: string | null;
  ip_type: 'statico' | 'dhcp';
  stato: 'assegnato' | 'disponibile' | 'conflitto' | 'riservato';
  device_id: string | null;
  lease_scadenza: string | null;
  assegnato_da: string;
  ultima_visita: string | null;
  created_at: string;
  subnet?: { nome: string; cidr: string } | null;
  vlan?: { nome: string; vlan_id: number } | null;
}

const statusConfig: Record<string, { label: string; bg: string; text: string; border: string }> = {
  assegnato: { label: 'Assegnato', bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20' },
  disponibile: { label: 'Disponibile', bg: 'bg-sky-500/10', text: 'text-sky-400', border: 'border-sky-500/20' },
  conflitto: { label: 'Conflitto', bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
  riservato: { label: 'Riservato', bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' },
};

export default function IPManagementContent() {
  const [records, setRecords] = useState<IPRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [subnetFilter, setSubnetFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [allocateOpen, setAllocateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<IPRecord | null>(null);
  const [sortCol, setSortCol] = useState('ip');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [releaseConfirm, setReleaseConfirm] = useState(false);
  const [subnets, setSubnets] = useState<any[]>([]);
  const [vlans, setVlans] = useState<any[]>([]);
  const [newIP, setNewIP] = useState({ ip: '', hostname: '', mac_address: '', ip_type: 'statico', subnet_id: '', vlan_id: '', stato: 'assegnato' });
  const [editIP, setEditIP] = useState({ ip: '', hostname: '', mac_address: '', ip_type: 'statico', subnet_id: '', vlan_id: '', stato: 'assegnato' });
  const [saving, setSaving] = useState(false);
  const pageSize = 10;
  const { user, canWrite } = useAuth();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: ips }, { data: sn }, { data: vl }] = await Promise.all([
        supabase.from('ip_addresses').select('*, subnet:subnets(nome, cidr), vlan:vlans(nome, vlan_id)').order('ip'),
        supabase.from('subnets').select('id, nome, cidr').order('nome'),
        supabase.from('vlans').select('id, nome, vlan_id').order('vlan_id'),
      ]);
      setRecords((ips || []) as IPRecord[]);
      setSubnets(sn || []);
      setVlans(vl || []);
    } catch (err: any) {
      toast.error('Errore caricamento: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const channel = supabase
      .channel('ip_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ip_addresses' }, fetchData)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchData]);

  const filtered = useMemo(() => {
    return records
      .filter(r => {
        const q = search.toLowerCase();
        const matchSearch = !q || r.ip.includes(q) || (r.hostname || '').toLowerCase().includes(q) || (r.mac_address || '').toLowerCase().includes(q);
        const matchSubnet = subnetFilter === 'all' || r.subnet_id === subnetFilter;
        const matchStatus = statusFilter === 'all' || r.stato === statusFilter;
        const matchType = typeFilter === 'all' || r.ip_type === typeFilter;
        return matchSearch && matchSubnet && matchStatus && matchType;
      })
      .sort((a, b) => {
        const av = (a as any)[sortCol] || '';
        const bv = (b as any)[sortCol] || '';
        return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      });
  }, [records, search, subnetFilter, statusFilter, typeFilter, sortCol, sortDir]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const pageData = filtered.slice((page - 1) * pageSize, page * pageSize);
  const conflictCount = records.filter(r => r.stato === 'conflitto').length;

  const toggleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const toggleAll = () => {
    if (selectedIds.size === pageData.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(pageData.map(r => r.id)));
  };

  const toggleRow = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleRelease = async () => {
    setReleaseConfirm(false);
    const ids = Array.from(selectedIds);
    try {
      const { error } = await supabase
        .from('ip_addresses')
        .update({ stato: 'disponibile', hostname: null, mac_address: null, device_id: null, assegnato_da: '' })
        .in('id', ids);
      if (error) throw error;
      toast.success(`${ids.length} IP rilasciato/i`);
      setSelectedIds(new Set());
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const validateIPv4 = (ip: string) => /^(\d{1,3}\.){3}\d{1,3}$/.test(ip) && ip.split('.').every(n => parseInt(n) <= 255);
  const validateMAC = (mac: string) => !mac || /^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/.test(mac);

  const handleAllocate = async () => {
    if (!newIP.ip) { toast.error('Inserisci un indirizzo IP'); return; }
    if (!validateIPv4(newIP.ip)) { toast.error('Formato IP non valido (es. 192.168.1.50)'); return; }
    if (!validateMAC(newIP.mac_address)) { toast.error('Formato MAC non valido (es. 00:1A:2B:3C:4D:5E)'); return; }
    // Check for conflicts
    const existing = records.find(r => r.ip === newIP.ip);
    if (existing) { toast.error(`IP ${newIP.ip} già presente (stato: ${existing.stato})`); return; }
    setSaving(true);
    try {
      const { error } = await supabase.from('ip_addresses').insert({
        ip: newIP.ip,
        hostname: newIP.hostname || null,
        mac_address: newIP.mac_address || null,
        ip_type: newIP.ip_type,
        stato: newIP.stato,
        subnet_id: newIP.subnet_id || null,
        vlan_id: newIP.vlan_id || null,
        assegnato_da: user?.email || 'admin',
        created_by: user?.id || null,
      });
      if (error) throw error;
      await supabase.from('activity_log').insert({
        tipo: 'ip_assegnato',
        messaggio: `IP ${newIP.ip} assegnato`,
        ip_address: newIP.ip,
        user_id: user?.id || null,
      });
      toast.success('IP assegnato con successo');
      setAllocateOpen(false);
      setNewIP({ ip: '', hostname: '', mac_address: '', ip_type: 'statico', subnet_id: '', vlan_id: '', stato: 'assegnato' });
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (r: IPRecord) => {
    setEditRecord(r);
    setEditIP({
      ip: r.ip,
      hostname: r.hostname || '',
      mac_address: r.mac_address || '',
      ip_type: r.ip_type,
      subnet_id: r.subnet_id || '',
      vlan_id: r.vlan_id || '',
      stato: r.stato,
    });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!editRecord) return;
    if (!validateIPv4(editIP.ip)) { toast.error('Formato IP non valido'); return; }
    if (!validateMAC(editIP.mac_address)) { toast.error('Formato MAC non valido (es. 00:1A:2B:3C:4D:5E)'); return; }
    // Check conflict with other records
    const conflict = records.find(r => r.ip === editIP.ip && r.id !== editRecord.id);
    if (conflict) { toast.error(`IP ${editIP.ip} già assegnato a un altro record`); return; }
    setSaving(true);
    try {
      const { error } = await supabase.from('ip_addresses').update({
        ip: editIP.ip,
        hostname: editIP.hostname || null,
        mac_address: editIP.mac_address || null,
        ip_type: editIP.ip_type,
        stato: editIP.stato,
        subnet_id: editIP.subnet_id || null,
        vlan_id: editIP.vlan_id || null,
      }).eq('id', editRecord.id);
      if (error) throw error;
      toast.success('IP aggiornato');
      setEditOpen(false);
      fetchData();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: 'ip', label: 'Indirizzo IP', sortable: true },
    { key: 'hostname', label: 'Hostname', sortable: true },
    { key: 'mac_address', label: 'MAC', sortable: false },
    { key: 'ip_type', label: 'Tipo', sortable: false },
    { key: 'stato', label: 'Stato', sortable: true },
    { key: 'subnet', label: 'Subnet', sortable: false },
    { key: 'vlan', label: 'VLAN', sortable: false },
    { key: 'ultima_visita', label: 'Ultima Visita', sortable: false },
    { key: 'lease_scadenza', label: 'Scadenza Lease', sortable: false },
  ];

  return (
    <div className="min-h-full bg-background">
      <Toaster position="bottom-right" theme="dark" richColors />
      <Breadcrumb items={[{ label: 'Gestione IP' }]} />

      <ConfirmDialog
        open={releaseConfirm}
        title="Rilascia indirizzi IP"
        message={`Sei sicuro di voler rilasciare ${selectedIds.size} indirizzo/i IP? Verranno resi disponibili per nuove assegnazioni.`}
        confirmLabel="Rilascia"
        onConfirm={handleRelease}
        onCancel={() => setReleaseConfirm(false)}
        danger
      />

      {/* Allocate Modal */}
      {allocateOpen && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between px-6 py-5 border-b border-border">
              <h2 className="text-lg font-bold text-white">Assegna Indirizzo IP</h2>
              <button onClick={() => setAllocateOpen(false)} className="p-2 rounded-lg hover:bg-surface-elevated transition-colors">
                <Icon name="XMarkIcon" size={18} className="text-muted-color" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-2">Indirizzo IP *</label>
                <input
                  value={newIP.ip}
                  onChange={e => setNewIP(p => ({ ...p, ip: e.target.value }))}
                  placeholder="es. 192.168.1.50"
                  className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base font-mono text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 transition-colors"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Hostname</label>
                  <input
                    value={newIP.hostname}
                    onChange={e => setNewIP(p => ({ ...p, hostname: e.target.value }))}
                    placeholder="es. server-01"
                    className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">MAC Address</label>
                  <input
                    value={newIP.mac_address}
                    onChange={e => setNewIP(p => ({ ...p, mac_address: e.target.value }))}
                    placeholder="00:1A:2B:3C:4D:5E"
                    className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base font-mono text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 transition-colors"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Tipo</label>
                  <select
                    value={newIP.ip_type}
                    onChange={e => setNewIP(p => ({ ...p, ip_type: e.target.value }))}
                    className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors"
                  >
                    <option value="statico">Statico</option>
                    <option value="dhcp">DHCP</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Stato</label>
                  <select
                    value={newIP.stato}
                    onChange={e => setNewIP(p => ({ ...p, stato: e.target.value }))}
                    className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors"
                  >
                    <option value="assegnato">Assegnato</option>
                    <option value="riservato">Riservato</option>
                    <option value="disponibile">Disponibile</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Subnet</label>
                  <select
                    value={newIP.subnet_id}
                    onChange={e => setNewIP(p => ({ ...p, subnet_id: e.target.value }))}
                    className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors"
                  >
                    <option value="">Nessuna</option>
                    {subnets.map(s => <option key={s.id} value={s.id}>{s.nome} ({s.cidr})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">VLAN</label>
                  <select
                    value={newIP.vlan_id}
                    onChange={e => setNewIP(p => ({ ...p, vlan_id: e.target.value }))}
                    className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors"
                  >
                    <option value="">Nessuna</option>
                    {vlans.map(v => <option key={v.id} value={v.id}>VLAN {v.vlan_id} — {v.nome}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button onClick={() => setAllocateOpen(false)} className="flex-1 py-3 rounded-xl border border-border text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={handleAllocate} disabled={saving} className="flex-1 py-3 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-white text-sm font-bold transition-all">{saving ? 'Salvataggio...' : 'Assegna IP'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editOpen && editRecord && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between px-6 py-5 border-b border-border">
              <h2 className="text-lg font-bold text-white">Modifica IP {editRecord.ip}</h2>
              <button onClick={() => setEditOpen(false)} className="p-2 rounded-lg hover:bg-surface-elevated transition-colors">
                <Icon name="XMarkIcon" size={18} className="text-muted-color" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-2">Indirizzo IP *</label>
                <input value={editIP.ip} onChange={e => setEditIP(p => ({ ...p, ip: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base font-mono text-white focus:outline-none focus:border-sky-500 transition-colors" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Hostname</label>
                  <input value={editIP.hostname} onChange={e => setEditIP(p => ({ ...p, hostname: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">MAC Address</label>
                  <input value={editIP.mac_address} onChange={e => setEditIP(p => ({ ...p, mac_address: e.target.value }))} placeholder="00:1A:2B:3C:4D:5E" className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base font-mono text-white focus:outline-none focus:border-sky-500 transition-colors" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Tipo</label>
                  <select value={editIP.ip_type} onChange={e => setEditIP(p => ({ ...p, ip_type: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors">
                    <option value="statico">Statico</option>
                    <option value="dhcp">DHCP</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Stato</label>
                  <select value={editIP.stato} onChange={e => setEditIP(p => ({ ...p, stato: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors">
                    <option value="assegnato">Assegnato</option>
                    <option value="riservato">Riservato</option>
                    <option value="disponibile">Disponibile</option>
                    <option value="conflitto">Conflitto</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">Subnet</label>
                  <select value={editIP.subnet_id} onChange={e => setEditIP(p => ({ ...p, subnet_id: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors">
                    <option value="">Nessuna</option>
                    {subnets.map(s => <option key={s.id} value={s.id}>{s.nome} ({s.cidr})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-2">VLAN</label>
                  <select value={editIP.vlan_id} onChange={e => setEditIP(p => ({ ...p, vlan_id: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-xl px-4 py-3 text-base text-white focus:outline-none focus:border-sky-500 transition-colors">
                    <option value="">Nessuna</option>
                    {vlans.map(v => <option key={v.id} value={v.id}>VLAN {v.vlan_id} — {v.nome}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button onClick={() => setEditOpen(false)} className="flex-1 py-3 rounded-xl border border-border text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={handleEdit} disabled={saving} className="flex-1 py-3 rounded-xl bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-white text-sm font-bold transition-all">{saving ? 'Salvataggio...' : 'Salva Modifiche'}</button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 py-6 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-white">Gestione Indirizzi IP</h1>
            <p className="text-base text-secondary-color mt-1">
              {loading ? 'Caricamento...' : (
                <>
                  {records.length} indirizzi monitorati ·{' '}
                  {conflictCount > 0 && <span className="text-red-400 font-semibold">{conflictCount} conflitti · </span>}
                  <span className="text-sky-400 font-semibold">{records.filter(r => r.stato === 'disponibile').length} disponibili</span>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-2 bg-surface border border-border rounded-lg px-4 py-2.5 text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">
              <Icon name="ArrowDownTrayIcon" size={17} />
              Esporta CSV
            </button>
            {canWrite() && (
              <button
                onClick={() => setAllocateOpen(true)}
                className="flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-white rounded-lg px-5 py-2.5 text-sm font-bold transition-all active:scale-95"
              >
                <Icon name="PlusIcon" size={18} />
                Assegna IP
              </button>
            )}
          </div>
        </div>

        {/* Conflict banner */}
        {conflictCount > 0 && (
          <div className="flex items-center gap-3 bg-red-500/8 border border-red-500/25 rounded-xl px-5 py-4">
            <Icon name="ExclamationTriangleIcon" size={18} className="text-red-400 flex-shrink-0" />
            <p className="text-base text-red-400 font-semibold">
              {conflictCount} IP in conflitto rilevato/i — righe evidenziate in rosso
            </p>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-48">
            <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-color" />
            <input
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Cerca IP, hostname, MAC..."
              className="w-full bg-surface border border-border rounded-lg pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 transition-colors"
            />
          </div>
          <select value={subnetFilter} onChange={e => { setSubnetFilter(e.target.value); setPage(1); }} className="bg-surface border border-border rounded-lg px-3 py-2.5 text-sm text-secondary-color focus:outline-none focus:border-sky-500 transition-colors">
            <option value="all">Tutte le subnet</option>
            {subnets.map(s => <option key={s.id} value={s.id}>{s.nome} ({s.cidr})</option>)}
          </select>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="bg-surface border border-border rounded-lg px-3 py-2.5 text-sm text-secondary-color focus:outline-none focus:border-sky-500 transition-colors">
            <option value="all">Tutti gli stati</option>
            <option value="assegnato">Assegnato</option>
            <option value="disponibile">Disponibile</option>
            <option value="conflitto">Conflitto</option>
            <option value="riservato">Riservato</option>
          </select>
          <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} className="bg-surface border border-border rounded-lg px-3 py-2.5 text-sm text-secondary-color focus:outline-none focus:border-sky-500 transition-colors">
            <option value="all">Tutti i tipi</option>
            <option value="statico">Statico</option>
            <option value="dhcp">DHCP</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-surface border border-border rounded-xl overflow-hidden">
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 px-4 py-3 bg-sky-500/8 border-b border-sky-500/20">
              <span className="text-base text-sky-400 font-semibold">{selectedIds.size} selezionati</span>
              {canWrite() && (
                <button onClick={() => setReleaseConfirm(true)} className="flex items-center gap-1.5 text-sm bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 rounded-lg px-4 py-2 transition-all font-semibold">
                  <Icon name="TrashIcon" size={15} />
                  Rilascia Selezionati
                </button>
              )}
              <button onClick={() => setSelectedIds(new Set())} className="text-sm text-secondary-color hover:text-white transition-colors font-semibold">Annulla</button>
            </div>
          )}

          {loading ? (
            <div className="flex flex-col items-center gap-4 py-16">
              <svg className="animate-spin w-8 h-8 text-sky-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <p className="text-base text-secondary-color">Caricamento indirizzi IP...</p>
            </div>
          ) : (
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-xs min-w-[900px]">
                <thead>
                  <tr className="border-b border-border bg-surface-elevated/50">
                    <th className="w-10 px-4 py-3">
                      <input type="checkbox" checked={selectedIds.size === pageData.length && pageData.length > 0} onChange={toggleAll} className="w-3.5 h-3.5 rounded border-border bg-surface accent-sky-500" />
                    </th>
                    {columns.map(col => (
                      <th key={col.key} className={`px-4 py-3 text-left font-medium text-muted-color tracking-wide uppercase text-[11px] ${col.sortable ? 'cursor-pointer hover:text-secondary-color select-none' : ''}`} onClick={() => col.sortable && toggleSort(col.key)}>
                        <span className="flex items-center gap-1">
                          {col.label}
                          {col.sortable && sortCol === col.key && <Icon name={sortDir === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={12} className="text-sky-400" />}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageData.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <Icon name="GlobeAltIcon" size={36} className="text-muted-color" />
                          <p className="text-base font-semibold text-secondary-color">Nessun indirizzo IP trovato</p>
                        </div>
                      </td>
                    </tr>
                  ) : pageData.map((r, idx) => {
                    const sc = statusConfig[r.stato] || statusConfig.disponibile;
                    const isConflict = r.stato === 'conflitto';
                    return (
                      <tr key={r.id} className={`border-b border-border/50 transition-colors ${isConflict ? 'bg-red-500/5' : idx % 2 !== 0 ? 'bg-surface-elevated/20' : ''} hover:bg-surface-elevated/40`}>
                        <td className="px-4 py-3">
                          <input type="checkbox" checked={selectedIds.has(r.id)} onChange={() => toggleRow(r.id)} className="w-3.5 h-3.5 rounded border-border bg-surface accent-sky-500" />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {isConflict && <Icon name="ExclamationTriangleIcon" size={12} className="text-red-400 flex-shrink-0" />}
                            <span className="font-mono font-semibold text-white">{r.ip}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-mono text-secondary-color">{r.hostname || '—'}</td>
                        <td className="px-4 py-3 font-mono text-secondary-color">{r.mac_address || '—'}</td>
                        <td className="px-4 py-3">
                          <span className="text-secondary-color capitalize">{r.ip_type === 'statico' ? 'Statico' : 'DHCP'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${sc.bg} ${sc.text} ${sc.border}`}>
                            {sc.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-secondary-color">{(r.subnet as any)?.cidr || '—'}</td>
                        <td className="px-4 py-3 text-secondary-color">
                          {(r.vlan as any) ? `VLAN ${(r.vlan as any).vlan_id}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-muted-color">
                          {r.ultima_visita ? new Date(r.ultima_visita).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                        </td>
                        <td className="px-4 py-3 text-muted-color">
                          {r.lease_scadenza ? new Date(r.lease_scadenza).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          {canWrite() && (
                            <button onClick={() => openEdit(r)} className="p-1.5 rounded-lg hover:bg-sky-500/10 text-muted-color hover:text-sky-400 transition-colors" title="Modifica">
                              <Icon name="PencilIcon" size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <p className="text-sm text-muted-color tabular-nums">
              {filtered.length === 0 ? 'Nessun risultato' : `Mostrando ${Math.min((page - 1) * pageSize + 1, filtered.length)}–${Math.min(page * pageSize, filtered.length)} di ${filtered.length} indirizzi`}
            </p>
            <div className="flex items-center gap-1">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="p-1.5 rounded-lg hover:bg-surface-elevated disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <Icon name="ChevronLeftIcon" size={14} className="text-secondary-color" />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setPage(p)} className={`w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${page === p ? 'bg-sky-500 text-white' : 'text-secondary-color hover:bg-surface-elevated'}`}>{p}</button>
              ))}
              <button disabled={page === totalPages || totalPages === 0} onClick={() => setPage(p => p + 1)} className="p-1.5 rounded-lg hover:bg-surface-elevated disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <Icon name="ChevronRightIcon" size={14} className="text-secondary-color" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}